import type {
    FC,
    InputHTMLAttributes,
    ReactNode,
    SelectHTMLAttributes,
    TextareaHTMLAttributes
} from 'react'

import { useState } from 'react'
import { Check, ChevronDown, CircleAlert, CircleCheck, Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/utils'

const CONTROL = 'w-full rounded-xl border bg-ivory px-4 text-[15px] text-burgundy outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-stone-light focus:border-burgundy/50 focus:ring-4 focus:ring-gold/30 focus-visible:outline-none disabled:opacity-60'

const controlState = (error?: string): string => error ? 'border-negative/60' : 'border-burgundy/15 hover:border-burgundy/30'

interface ShellProps {
    id: string
    label: string
    error?: string
    hint?: string
    optional?: boolean
    className?: string
    children: ReactNode
}

const FieldShell: FC<ShellProps> = ({ id, label, error, hint, optional, className, children }): ReactNode => {
    return (
        <div className={className}>
            <label htmlFor={id} className='flex items-baseline justify-between gap-3 text-sm font-medium text-burgundy'>
                {label}
                {optional && <span className='text-xs font-normal text-stone'>Optional</span>}
            </label>

            <div className='mt-2'>{children}</div>

            {error ? (
                <p id={`${id}-error`} className='mt-2 flex items-center gap-1.5 text-xs font-medium text-negative'>
                    <CircleAlert aria-hidden='true' className='size-3.5 shrink-0' />
                    {error}
                </p>
            ) : hint ? (
                <p id={`${id}-hint`} className='mt-2 text-xs text-stone'>{hint}</p>
            ) : null}
        </div>
    )
}

const describedBy = (id: string, error?: string, hint?: string): string | undefined => {
    if (error) return `${id}-error`
    if (hint) return `${id}-hint`

    return undefined
}

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
    id: string
    label: string
    error?: string
    hint?: string
    optional?: boolean
    wrapperClassName?: string
}

export const TextField: FC<TextFieldProps> = ({
    id,
    label,
    error,
    hint,
    optional,
    wrapperClassName,
    className,
    ...rest
}): ReactNode => {
    return (
        <FieldShell id={id} label={label} error={error} hint={hint} optional={optional} className={wrapperClassName}>
            <input
                id={id}
                aria-invalid={error ? true : undefined}
                aria-describedby={describedBy(id, error, hint)}
                className={cn(CONTROL, 'h-12', controlState(error), className)}
                {...rest}
            />
        </FieldShell>
    )
}

export const PasswordField: FC<TextFieldProps> = ({
    id,
    label,
    error,
    hint,
    optional,
    wrapperClassName,
    className,
    ...rest
}): ReactNode => {
    const [visible, setVisible] = useState(false)

    return (
        <FieldShell id={id} label={label} error={error} hint={hint} optional={optional} className={wrapperClassName}>
            <div className='relative'>
                <input
                    id={id}
                    type={visible ? 'text' : 'password'}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={describedBy(id, error, hint)}
                    className={cn(CONTROL, 'h-12 pr-12', controlState(error), className)}
                    {...rest}
                />

                <button
                    type='button'
                    onClick={() => setVisible(value => !value)}
                    aria-label={visible ? 'Hide password' : 'Show password'}
                    aria-pressed={visible}
                    className='absolute right-2 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-lg text-stone transition-colors hover:text-burgundy'
                >
                    {visible ? <EyeOff aria-hidden='true' className='size-4' /> : <Eye aria-hidden='true' className='size-4' />}
                </button>
            </div>
        </FieldShell>
    )
}

interface TextAreaFieldProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
    id: string
    label: string
    error?: string
    hint?: string
    optional?: boolean
    wrapperClassName?: string
}

export const TextAreaField: FC<TextAreaFieldProps> = ({
    id,
    label,
    error,
    hint,
    optional,
    wrapperClassName,
    className,
    ...rest
}): ReactNode => {
    return (
        <FieldShell id={id} label={label} error={error} hint={hint} optional={optional} className={wrapperClassName}>
            <textarea
                id={id}
                aria-invalid={error ? true : undefined}
                aria-describedby={describedBy(id, error, hint)}
                className={cn(CONTROL, 'min-h-[140px] resize-y py-3 leading-relaxed', controlState(error), className)}
                {...rest}
            />
        </FieldShell>
    )
}

interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
    id: string
    label: string
    options: Array<string | { value: string, label: string }>
    placeholder?: string
    error?: string
    hint?: string
    optional?: boolean
    wrapperClassName?: string
}

export const SelectField: FC<SelectFieldProps> = ({
    id,
    label,
    options,
    placeholder,
    error,
    hint,
    optional,
    wrapperClassName,
    className,
    ...rest
}): ReactNode => {
    return (
        <FieldShell id={id} label={label} error={error} hint={hint} optional={optional} className={wrapperClassName}>
            <div className='relative'>
                <select
                    id={id}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={describedBy(id, error, hint)}
                    className={cn(CONTROL, 'h-12 cursor-pointer appearance-none pr-11', controlState(error), className)}
                    {...rest}
                >
                    {placeholder !== undefined && <option value=''>{placeholder}</option>}

                    {options.map(option => {
                        const item = typeof option === 'string' ? { value: option, label: option } : option

                        return <option key={item.value} value={item.value}>{item.label}</option>
                    })}
                </select>

                <ChevronDown aria-hidden='true' className='pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-stone' />
            </div>
        </FieldShell>
    )
}

interface CheckboxFieldProps {
    id: string
    checked: boolean
    onChange: (checked: boolean) => void
    children: ReactNode
    error?: string
}

export const CheckboxField: FC<CheckboxFieldProps> = ({ id, checked, onChange, children, error }): ReactNode => {
    return (
        <div>
            <label htmlFor={id} className='flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-stone-dark'>
                <span className='relative mt-0.5 flex size-5 shrink-0'>
                    <input
                        id={id}
                        type='checkbox'
                        checked={checked}
                        onChange={event => onChange(event.target.checked)}
                        aria-invalid={error ? true : undefined}
                        aria-describedby={error ? `${id}-error` : undefined}
                        className={cn(
                            'peer size-5 cursor-pointer appearance-none rounded-md border bg-ivory transition-colors checked:border-burgundy checked:bg-burgundy focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-dark',
                            error ? 'border-negative/60' : 'border-burgundy/25'
                        )}
                    />

                    <Check aria-hidden='true' className='pointer-events-none absolute inset-0 m-auto size-3.5 text-cream opacity-0 peer-checked:opacity-100' />
                </span>

                <span>{children}</span>
            </label>

            {error && (
                <p id={`${id}-error`} className='mt-2 flex items-center gap-1.5 text-xs font-medium text-negative'>
                    <CircleAlert aria-hidden='true' className='size-3.5 shrink-0' />
                    {error}
                </p>
            )}
        </div>
    )
}

interface ChipGroupProps {
    id: string
    label: string
    options: Array<string>
    value: Array<string>
    onChange: (value: Array<string>) => void
    hint?: string
    error?: string
}

/** Multi-select made of toggle buttons. */
export const ChipGroup: FC<ChipGroupProps> = ({ id, label, options, value, onChange, hint, error }): ReactNode => {
    const toggle = (option: string) => {
        onChange(value.includes(option) ? value.filter(item => item !== option) : [...value, option])
    }

    return (
        <fieldset aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}>
            <legend className='flex w-full items-baseline justify-between gap-3 text-sm font-medium text-burgundy'>
                {label}
                <span className='text-xs font-normal text-stone'>Optional</span>
            </legend>

            <div className='mt-3 flex flex-wrap gap-2'>
                {options.map(option => {
                    const selected = value.includes(option)

                    return (
                        <button
                            key={option}
                            type='button'
                            aria-pressed={selected}
                            onClick={() => toggle(option)}
                            className={cn(
                                'inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm transition-colors duration-300',
                                selected ? 'border-burgundy bg-burgundy text-cream' : 'border-burgundy/15 bg-ivory text-stone-dark hover:border-burgundy/35 hover:text-burgundy'
                            )}
                        >
                            {selected && <Check aria-hidden='true' className='size-3.5' />}
                            {option}
                        </button>
                    )
                })}
            </div>

            {error ? (
                <p id={`${id}-error`} className='mt-2 text-xs font-medium text-negative'>{error}</p>
            ) : hint ? (
                <p id={`${id}-hint`} className='mt-2 text-xs text-stone'>{hint}</p>
            ) : null}
        </fieldset>
    )
}

interface FormAlertProps {
    tone: 'error' | 'success'
    children: ReactNode
    className?: string
}

export const FormAlert: FC<FormAlertProps> = ({ tone, children, className }): ReactNode => {
    const Icon = tone === 'error' ? CircleAlert : CircleCheck

    return (
        <div
            role={tone === 'error' ? 'alert' : 'status'}
            className={cn(
                'flex items-start gap-3 rounded-xl border px-4 py-3 text-sm',
                tone === 'error' ? 'border-negative/25 bg-negative/[0.06] text-negative' : 'border-positive/25 bg-positive/[0.06] text-positive',
                className
            )}
        >
            <Icon aria-hidden='true' className='mt-0.5 size-4 shrink-0' />
            <div>{children}</div>
        </div>
    )
}

/** Hidden field that only bots fill in. Submissions with a value are dropped. */
export const Honeypot: FC<{ value: string, onChange: (value: string) => void }> = ({ value, onChange }): ReactNode => {
    return (
        <div aria-hidden='true' className='absolute -left-[9999px] top-auto h-px w-px overflow-hidden'>
            <label htmlFor='website-field'>Website</label>
            <input
                id='website-field'
                name='website'
                type='text'
                tabIndex={-1}
                autoComplete='off'
                value={value}
                onChange={event => onChange(event.target.value)}
            />
        </div>
    )
}
